import { useEffect, useMemo, useRef } from 'react';
import { api } from '../lib/api';
import { useMe } from '../lib/me';
import { isOwnerPick, setOwnerPick, useOwnerPick, type OwnerPick } from '../lib/ownerPick';

/**
 * 머리줄 「담당자」(2026-11-02 way — 「모든 메뉴에 버스인 접수담당자 드롭다운 · 여러 사람을 보는 범위(팀·전체)일 때만 활성」).
 *
 * 고르면 접수·배차·정산·재무추가·월정기·할부금 등 **담당 범위로 거르는 목록 전부**가 그 사람(주 담당·함께 담당) 것만 보인다.
 * 기사·차주 목록은 「그 사람 담당 접수를 운행한 기사·차주」 — 계정 범위와 같은 규칙이다(SPEC §8).
 * 계정에 기억한다(`owner.pick` — 다른 PC 에서도 같음). 고른 동안은 노란색으로 서 있다(조용한 거르기 금지).
 * 본인 범위뿐인 계정은 꺼진 채 「본인」으로만 보인다. 강제는 서버(`shared.ts withPick`).
 */
const KEY = 'owner.pick';

export default function OwnerPickSelect({ className = '' }: { className?: string }) {
  const { me } = useMe();
  const pick = useOwnerPick();
  const opts = me?.ownerPick ?? null;

  // 계정에 기억한 값 — 처음 한 번. 목록에 없는 사람(범위가 바뀜)이면 고르지 않음으로.
  const init = useRef<string | null>(null);
  useEffect(() => {
    if (!me || init.current === me.email) return;
    init.current = me.email;
    const saved = me.prefs?.[KEY];
    if (!opts || !isOwnerPick(saved) || saved === null) return;
    if (saved === 'none' || opts.rows.some((r) => r.id === saved)) setOwnerPick(saved);
  }, [me, opts]);

  const groups = useMemo(() => {
    const m = new Map<string, { id: number; name: string; active: boolean }[]>();
    for (const r of opts?.rows ?? []) {
      const k = r.active ? (r.team || '팀 없음') : '퇴사·비활성';
      m.set(k, [...(m.get(k) ?? []), r]);
    }
    return [...m.entries()].sort(([a], [b]) =>
      (a === '퇴사·비활성' ? 1 : 0) - (b === '퇴사·비활성' ? 1 : 0) || a.localeCompare(b, 'ko'));
  }, [opts]);

  if (!me) return null;
  const choose = (v: OwnerPick) => {
    setOwnerPick(v);
    api.setMyPref({ key: KEY, value: v }).catch(() => undefined);
    me.prefs = { ...(me.prefs ?? {}), [KEY]: v };
  };
  const on = pick !== null && !!opts;
  const name = pick === 'none' ? '담당 미지정' : opts?.rows.find((r) => r.id === pick)?.name;

  return (
    <label
      className={`flex shrink-0 items-center gap-1 whitespace-nowrap rounded-lg px-1.5 py-0.5 text-sm ${on ? 'bg-amber-100 text-amber-900 ring-1 ring-amber-400' : 'text-ink-soft'} ${className}`}
      title={!opts
        ? '본인 담당 범위라 다른 담당자를 고를 수 없습니다. 범위는 관리자가 사용자 관리에서 정합니다.'
        : on
          ? `${name} 담당(함께 담당 포함) 접수와 그 접수를 운행한 기사·차주만 보고 있습니다. 「전체」로 되돌립니다.`
          : '버스인 접수담당자를 고르면 모든 메뉴 목록이 그 사람 담당 접수만 보입니다.'}
    >
      <span className="text-xs">버스인접수담당자</span>
      <select
        className="max-w-[9rem] rounded border border-line bg-surface px-1 py-0.5 text-sm disabled:opacity-60"
        disabled={!opts}
        value={!opts ? 'own' : pick === null ? '' : String(pick)}
        onChange={(e) => {
          const v = e.target.value;
          choose(v === '' ? null : v === 'none' ? 'none' : Number(v));
        }}
      >
        {!opts ? <option value="own">본인</option> : (
          <>
            <option value="">전체</option>
            <option value="none">담당 미지정</option>
            {groups.map(([team, rows]) => (
              <optgroup key={team} label={team}>
                {rows.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </optgroup>
            ))}
          </>
        )}
      </select>
    </label>
  );
}
